import { WatcherrApiError, WatcherrApiServiceUnavailableError, watcherrErrorTranslationKeys } from '@ixicare/resources';
import { t } from '~i18n';

/**
 * The backend refuses a login that would take a site past its maximum concurrent alarm
 * receivers, and marks it with this code. Without it the response is a plain 403 and the
 * user is told they are "not authorised", which is both wrong and unactionable — the
 * credentials were fine, the site is simply full.
 */
const CONCURRENT_LIMIT_ERROR_CODE = 'concurrent_alarm_receiver_limit_reached';

const isConcurrentLimitError = (error: Error): boolean =>
  (error as { body?: { errorCode?: string } }).body?.errorCode === CONCURRENT_LIMIT_ERROR_CODE;

export const humaniseErrors = (error: Error): { title: string; description?: string; img?: string } => {
  if (isConcurrentLimitError(error)) {
    return { title: t('error.concurrentLimit.title'), description: t('error.concurrentLimit.description') };
  }

  if (error instanceof WatcherrApiError) {
    error.response
      .text()
      .then((body) => {
        console.warn('Showing error to user', error, body);
      })
      .catch(() => {
        console.warn('Showing error to user', error);
      });
  } else {
    console.warn('Showing error to user', error);
  }

  // Each half asks separately: a 403 or a 404 keeps its translated title while the description is
  // the backend's own sentence about this particular refusal, which there is no key for.
  const { title, description, translatable, descriptionTranslatable } = watcherrErrorTranslationKeys(error);
  return {
    title: translatable ? t(title) : title,
    description: description ? (descriptionTranslatable ? t(description) : description) : undefined,
  };
};

/**
 * What to tell the user when a service the backend depends on did not answer, or nothing when
 * that is not what happened. The backend names the service; the sentence is the same for any of
 * them, so the next service the backend proxies gets this for free once it has a display name.
 *
 * Says that nothing was saved on purpose: the one thing the user needs to know after a failed
 * save is whether to try again, and a timeout on the way to a service means the service never
 * saw the request.
 */
export const serviceUnavailableMessage = (error: unknown): string | undefined => {
  if (!(error instanceof WatcherrApiServiceUnavailableError)) return undefined;
  const service = error.service
    ? t(`error.serviceUnavailable.service.${error.service}`, { defaultValue: error.service })
    : t('error.serviceUnavailable.service.unknown');
  return t('error.serviceUnavailable.message', { service });
};

/** The message for a failed action: the unreachable-service sentence when that is the cause, the caller's otherwise. */
export const failureMessage = (error: unknown, fallback: string): string =>
  serviceUnavailableMessage(error) ?? fallback;

export const humaniseErrorToMessage = (error: Error) => {
  const { title, description } = humaniseErrors(error);
  return `${title}\n ${description}`;
};

export const errorStatus = (error: Error): number | undefined =>
  error instanceof WatcherrApiError ? error.response?.status : undefined;

// Re-exported so the callers that already import it from here need no change; the reasoning
// about key spelling and message shape lives beside the implementation.
export { getFieldError, hasFieldErrors } from './fieldErrors';
